import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn, toDataCy } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      // Filter out custom props that shouldn't be passed to DOM
      btnName,
      dataCy,
      errorText,
      name, // Filter name prop - not standard for button elements
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const textFromChildren = React.Children.toArray(props.children)
      .filter(
        (child) =>
          typeof child === "string" || typeof child === "number",
      )
      .join(" ");

    const fallbackDataCy =
      props["data-cy"] ??
      dataCy ??
      toDataCy(btnName) ??
      toDataCy(name) ??
      toDataCy(props.id) ??
      toDataCy(props.title) ??
      toDataCy(props["aria-label"]) ??
      toDataCy(textFromChildren);

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        data-cy={fallbackDataCy}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
